import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { FunctionGroupsDaoService } from '../../dao/function-groups-dao.service';
import { FunctionGroupsService } from '../function-groups.service';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { FunctionGroup, FunctionGroupDTO, FUNCTIONGROUP_SYSCODE_PCD } from 'src/app/_model/function-group';
import { Peripheral } from 'src/app/_model/peripheral';

@Injectable()
export class FunctionGroupLogicService implements FunctionGroupsService {
  public functionGroups$ = new BehaviorSubject<FunctionGroup[]>([]);
  public pcdFunctionGroups$ = new BehaviorSubject<FunctionGroup[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private functionGroupsDaoService: FunctionGroupsDaoService,
    private authenticationService: AuthenticationService
  ) {
    this.initialize();
  }

  // Public Functions
  public getFunctionGroupById(id: IMSId): FunctionGroup {
    if (id === undefined) {
      return undefined
    }

    return [...this.functionGroups$.getValue(), ...this.pcdFunctionGroups$.getValue()].find((fg) => {
      if (fg.id.imsId == id.imsId && fg.id.secondaryId == id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    await this.functionGroupsDaoService.reset();
  }

  public async clear() {
    await this.functionGroupsDaoService.clear();
    this.functionGroups$.next([]);
    this.pcdFunctionGroups$.next([]);
  }

  /**
   * Splits the full FunctionGroup list by syscode and publishes each part to
   * its own subject, so old (non-filtering) consumers of functionGroups$ never
   * see PCD-only function groups by accident.
   */
  private publishFunctionGroups(functionGroups: FunctionGroup[]): void {
    const pcd = functionGroups.filter((fg) => fg.syscode === FUNCTIONGROUP_SYSCODE_PCD);
      this.logger.log(this.constructor.name, '- Loaded local PCDFunctionGroups', pcd?.length);
    const rest = functionGroups.filter((fg) => fg.syscode !== FUNCTIONGROUP_SYSCODE_PCD);
      this.logger.log(this.constructor.name, '- Loaded local OtherFunctionGroups', rest?.length);
    this.functionGroups$.next(rest);
    this.pcdFunctionGroups$.next(pcd);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteFunctionGroups(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalFunctionGroups();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalFunctionGroups() {
    try {
      this.logger.log(this.constructor.name, '- Load local FunctionGroups');
      let functionGroups: FunctionGroup[] = await this.functionGroupsDaoService.getFunctionGroups();
      this.logger.log(this.constructor.name, '- Loaded local FunctionGroups', functionGroups?.length);

      this.publishFunctionGroups(functionGroups);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local FunctionGroups: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteFunctionGroups(apiLevel: APILevel, syncKey: string) {
    this.logger.log(
      this.constructor.name,
      '- loadRemoteFunctionGroups with APILevel',
      apiLevel
    );
    if (apiLevel < APILevel.FUNCTIONGROUPS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load FunctionGroups init with no FunctionGroups'
      );
      var functionGroups: FunctionGroup[] = [];
      await this.functionGroupsDaoService.saveFunctionGroups(functionGroups);
      await this.loadLocalFunctionGroups();
      return;
    }

    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load remote FunctionGroups from IMS'
      );

      try {
        let functionGroups: FunctionGroup[] = [];
        let functionGroupsDto: FunctionGroupDTO[] = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'syncfunctiongroups',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        this.logger.log(
          this.constructor.name,
          'Downloaded FunctionGroupDTO: ',
          functionGroupsDto?.length
        );

        for (const functionGroupDto of functionGroupsDto) {
          functionGroups.push(FunctionGroup.fromDto(functionGroupDto));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded FunctionGroups: ',
          functionGroups?.length
        );

        await this.functionGroupsDaoService.saveFunctionGroups(functionGroups);
        this.publishFunctionGroups(functionGroups);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading FunctionGroups: ',
          error?.message
        );
        throw error;
      }
    } else {
      let error = new Error(
        'Cannot download FunctionGroups - user not authenticated'
      );
      this.logger.error(this.constructor.name, error?.message);
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
